import React from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2 } from "lucide-react";

export function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-display block mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

export function TextField({ label, value, onChange, placeholder }) {
  return (
    <Field label={label}>
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-11"
      />
    </Field>
  );
}

export function TextAreaField({ label, value, onChange, rows = 4, placeholder }) {
  return (
    <Field label={label}>
      <Textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="resize-none text-sm"
      />
    </Field>
  );
}

export function ArrayEditor({ label, items, onChange, emptyItem, renderItem, addLabel = "+ 添加一项" }) {
  const updateAt = (i, next) => {
    const copy = [...items];
    copy[i] = next;
    onChange(copy);
  };
  const removeAt = (i) => {
    onChange(items.filter((_, idx) => idx !== i));
  };
  const add = () => onChange([...items, { ...emptyItem }]);

  return (
    <div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-display mb-3">
        {label}
      </div>
      <div className="space-y-4">
        {items?.map((item, i) => (
          <div key={i} className="bg-slate-50 border border-slate-200 rounded-sm p-4 relative">
            <button
              type="button"
              onClick={() => removeAt(i)}
              className="absolute top-3 right-3 text-slate-400 hover:text-red-600 transition-colors"
              aria-label="remove"
            >
              <Trash2 size={14} />
            </button>
            {renderItem(item, (next) => updateAt(i, next), i)}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="mt-3 inline-flex items-center gap-2 text-xs text-orange-600 hover:text-orange-700 font-medium"
      >
        <Plus size={12} /> {addLabel}
      </button>
    </div>
  );
}

export function TagsEditor({ label, tags, onChange }) {
  const update = (i, val) => {
    const copy = [...tags];
    copy[i] = val;
    onChange(copy);
  };
  const remove = (i) => onChange(tags.filter((_, idx) => idx !== i));
  const add = () => onChange([...(tags || []), ""]);

  return (
    <div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-display mb-3">
        {label}
      </div>
      <div className="space-y-2">
        {(tags || []).map((t, i) => (
          <div key={i} className="flex items-center gap-2">
            <Input value={t} onChange={(e) => update(i, e.target.value)} className="h-10 flex-1" />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-slate-400 hover:text-red-600"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="mt-3 inline-flex items-center gap-2 text-xs text-orange-600 hover:text-orange-700 font-medium"
      >
        <Plus size={12} /> 添加标签
      </button>
    </div>
  );
}

export function ParagraphsEditor({ label, paragraphs, onChange }) {
  const update = (i, val) => {
    const copy = [...paragraphs];
    copy[i] = val;
    onChange(copy);
  };
  const remove = (i) => onChange(paragraphs.filter((_, idx) => idx !== i));
  const add = () => onChange([...(paragraphs || []), ""]);

  return (
    <div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-display mb-3">
        {label}
      </div>
      <div className="space-y-3">
        {(paragraphs || []).map((p, i) => (
          <div key={i} className="flex items-start gap-2">
            <Textarea
              value={p}
              onChange={(e) => update(i, e.target.value)}
              rows={3}
              className="resize-none text-sm flex-1"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="text-slate-400 hover:text-red-600 mt-2"
            >
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="mt-3 inline-flex items-center gap-2 text-xs text-orange-600 hover:text-orange-700 font-medium"
      >
        <Plus size={12} /> 添加段落
      </button>
    </div>
  );
}
